import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { ArrowLeft, RotateCcw, MapPin } from 'lucide-react';
import { PhoneInput, normalizePhone } from '../ui/phone-input';
import { useSession } from '../../contexts/SessionContext';

const STATUS_PILL = {
  open:   'bg-emerald-400/30 text-emerald-100',
  won:    'bg-green-500/40 text-white',
  lost:   'bg-red-400/30 text-red-100',
  closed: 'bg-white/20 text-white/80',
};

const SessionHeader = () => {
  const navigate = useNavigate();
  const {
    session,
    isAdvisor,
    editingClientInfo, setEditingClientInfo,
    clientInfoForm, setClientInfoForm,
    savingClientInfo, handleSaveClientInfo,
    canReopenSession, handleReopenSession,
    canLogVisit, setVisitForm, setVisitDialogOpen,
  } = useSession();

  if (!session) return null;
  const pillCls = STATUS_PILL[session.status] || STATUS_PILL.closed;

  return (
    <>
      {/* Green header bar */}
      <div className="bg-[#075E54] text-white px-4 py-3 flex-shrink-0">
        <div className="flex items-start gap-2">
          <Button variant="ghost" size="icon" className="text-white hover:bg-emerald-700 flex-shrink-0 mt-0.5" onClick={() => navigate(-1)} data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-base font-semibold">{session.session_number}</h1>
              <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${pillCls}`}>{session.status}</span>
            </div>
            <p className="text-xs text-emerald-200 mt-0.5 flex items-center gap-1 flex-wrap">
              {session.client_name || session.client_phone ? (
                session.client_phone ? (
                  <button
                    className="hover:text-white hover:underline transition-colors text-left"
                    onClick={() => navigate(`/client-timeline/${encodeURIComponent(session.client_phone)}`)}
                    data-testid="view-client-timeline-btn"
                  >
                    {session.client_name || 'No name'} · {session.client_phone}
                  </button>
                ) : (
                  <span>{session.client_name}</span>
                )
              ) : (
                <span className="text-amber-300 italic">No client info yet</span>
              )}
              {session.status === 'open' && !editingClientInfo && !isAdvisor && (
                <button
                  onClick={() => {
                    setClientInfoForm({ client_name: session.client_name || '', client_phone: session.client_phone || '' });
                    setEditingClientInfo(true);
                  }}
                  className="text-emerald-300 hover:text-white transition-colors"
                  title="Edit client info"
                  data-testid="edit-client-info-btn"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                  </svg>
                </button>
              )}
            </p>
            <p className="text-[10px] text-emerald-300 mt-0.5">Salesman: {session.assigned_salesman_name}</p>
            {session.created_by_name && (
              <p className="text-[10px] text-emerald-400 mt-0.5">
                Created by {session.created_by_name}
                {session.created_at && ` · ${new Date(session.created_at + 'Z').toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: true })}`}
              </p>
            )}
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            {(session.status === 'closed' || session.status === 'won' || session.status === 'lost') && canReopenSession && (
              <Button size="sm" variant="ghost" className="text-white hover:bg-emerald-700 text-xs px-2" onClick={handleReopenSession} data-testid="reopen-session-header-btn">
                <RotateCcw className="w-3 h-3 mr-1" /> Reopen
              </Button>
            )}
            {session.status === 'open' && canLogVisit && (
              <Button
                size="sm"
                variant="ghost"
                className="text-white hover:bg-emerald-700 text-xs px-2"
                onClick={() => {
                  const today = new Date().toISOString().split('T')[0];
                  setVisitForm({ date: today, summary: '' });
                  setVisitDialogOpen(true);
                }}
                data-testid="log-visit-btn"
              >
                <MapPin className="w-3 h-3 mr-1" /> Log Visit
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Client Info Edit Panel — shown when the pencil is tapped */}
      {editingClientInfo && (
        <div className="bg-white border-b shadow-sm px-4 py-4 flex-shrink-0" data-testid="client-info-edit-form">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Edit Client Info</p>
          <div className="space-y-3">
            <div>
              <label className="text-xs text-gray-600 mb-1 block">Client Name *</label>
              <Input
                value={clientInfoForm.client_name}
                onChange={e => setClientInfoForm(f => ({ ...f, client_name: e.target.value }))}
                placeholder="Full name"
                className="h-10"
                data-testid="client-name-edit-input"
                autoFocus
              />
            </div>
            <div>
              <label className="text-xs text-gray-600 mb-1 block">Phone *</label>
              <PhoneInput
                value={normalizePhone(clientInfoForm.client_phone)}
                onChange={val => setClientInfoForm(f => ({ ...f, client_phone: val }))}
                placeholder="Phone number"
                inputProps={{ 'data-testid': 'client-phone-edit-input' }}
              />
            </div>
          </div>
          <div className="flex gap-2 mt-4">
            <Button variant="outline" className="flex-1 h-10" onClick={() => setEditingClientInfo(false)}>Cancel</Button>
            <Button
              className="flex-1 h-10 bg-[#075E54] hover:bg-[#064E46]"
              onClick={handleSaveClientInfo}
              disabled={savingClientInfo}
              data-testid="save-client-info-btn"
            >
              {savingClientInfo ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </div>
      )}
    </>
  );
};

export default SessionHeader;
